'use client'

import { useMemo } from 'react'
import SectionHeading from '@/components/SectionHeading'
import TestimonialCard from '@/components/TestimonialCard'
import { useI18n } from '@/lib/i18n/context'

// Exportés pour scripts/tests/vocabulaire-site-public.test.ts : une citation
// qui garde le mot « neutralité » (celui du client, pas le nôtre — décision
// Guillaume, 24/09/2026) doit toujours porter son attribution.
export const STATIC_TESTIMONIALS_FR = [
  {
    quote:
      "En partenariat avec Stock CO2, les ministères de la Transition écologique ont participé à un projet de reboisement dans les Hauts-de-France. Une collaboration exemplaire au service de la neutralité carbone.",
    author: 'Secrétariat général',
    role: 'Direction des services',
    company: 'Gouvernement',
    rating: 5,
  },
  {
    quote:
      "À l'occasion de notre 100ème congrès, l'ASTEE a souhaité aller plus loin en contribuant à la neutralité carbone à hauteur de ses émissions résiduelles. Stock CO2 nous a accompagnés avec rigueur et transparence.",
    author: 'Direction générale',
    company: 'ASTEE',
    rating: 5,
  },
  {
    quote:
      "Notre association avec Stock CO2 a permis à la Société Urbaine de Climatisation de contribuer à la lutte contre le réchauffement climatique en soutenant des projets de reboisement forestiers concrets.",
    author: 'Direction RSE',
    company: 'Société Urbaine de Climatisation — Dalkia',
    rating: 5,
  },
]

export const STATIC_TESTIMONIALS_EN = [
  {
    quote:
      'In partnership with Stock CO2, the French Ministries for Ecological Transition took part in a reforestation project in Hauts-de-France. An exemplary collaboration serving carbon neutrality.',
    author: 'General Secretariat',
    role: 'Department of Services',
    company: 'French Government',
    rating: 5,
  },
  {
    quote:
      "On the occasion of our 100th congress, ASTEE decided to go further by contributing to carbon neutrality for all its residual emissions. Stock CO2 supported us with rigour and transparency.",
    author: 'General Management',
    company: 'ASTEE',
    rating: 5,
  },
  {
    quote:
      'Our partnership with Stock CO2 allowed the Société Urbaine de Climatisation to contribute to the fight against climate change by supporting concrete forestry reforestation projects.',
    author: 'CSR Management',
    company: 'Société Urbaine de Climatisation — Dalkia',
    rating: 5,
  },
]

interface TestimonialsSectionProps {
  payloadTestimonials?: any[]
  /** Surtitre et titre de la section (accueil du 08/10/2026 : « Témoignages », « Ils en parlent »). */
  surtitre?: string
  titre?: string
}

export default function TestimonialsSection({ payloadTestimonials = [], surtitre, titre }: TestimonialsSectionProps) {
  const { t, locale } = useI18n()

  // Transform Payload testimonials to match the static format
  const testimonials = useMemo(() => {
    if (payloadTestimonials && payloadTestimonials.length > 0) {
      return payloadTestimonials.map((item: any) => ({
        quote: locale === 'en' ? item.quoteEn : item.quoteFr,
        author: item.authorName || '',
        role: item.authorRole || '',
        company: item.companyName || '',
        rating: item.rating || 5,
      }))
    }
    // Fallback to static testimonials
    return locale === 'en' ? STATIC_TESTIMONIALS_EN : STATIC_TESTIMONIALS_FR
  }, [payloadTestimonials, locale])

  // Accueil (titre fourni) : titre aligné à gauche, comme les autres sections de la page
  if (titre) {
    return (
      <section className="pb-20 md:pb-28 bg-cream" data-testid="accueil-temoignages">
        <div className="container-site">
          <h2 className="font-heading font-extrabold text-[30px] md:text-[40px] leading-[1.12] tracking-[-0.01em] text-[#14281F] mb-9">{titre}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {testimonials.map((testimonial, i) => (
              <TestimonialCard key={testimonial.company} {...testimonial} index={i} />
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="section-padding bg-sage/30">
      <div className="container-site">
        <SectionHeading
          badge={surtitre ?? t.testimonials.badge}
          title={t.testimonials.title}
          subtitle={t.testimonials.subtitle}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, i) => (
            <TestimonialCard key={testimonial.company} {...testimonial} index={i} />
          ))}
        </div>
      </div>
    </section>
  )
}
